import {Component, EventEmitter, Input, Output} from '@angular/core';
import {EventItem, League, Lookups} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import {NgbActiveModal} from "@ng-bootstrap/ng-bootstrap";
import {FormControl, FormGroup} from "@angular/forms";
import {AbstractComponent} from "../abstract.component";
import {max_length_100, max_length_255, required} from "@utility";
import {merge} from 'rxjs';
import {LeagueTemplateEventStatus} from "../../model/league/league-template-event-status";

@Component({
  selector: 'app-event-template-modal',
  templateUrl: './event-template-modal.component.html',
  styleUrl: './event-template-modal.component.scss',
  standalone: false
})
export class EventTemplateModalComponent extends AbstractComponent{

  @Input() templateEvent: EventItem | undefined
  @Input() title: string | undefined;
  @Input() league: League | undefined;
  @Input() sports: Lookups[] = [];
  @Input() countries: Lookups[] = [];
  @Output() onSave: EventEmitter<EventItem> = new EventEmitter<EventItem>();

  leagueTemplateStatuses: LeagueTemplateEventStatus[] = [LeagueTemplateEventStatus.EXTERNAL, LeagueTemplateEventStatus.CANCELLED]

  constructor(public translocoService: TranslocoService, public activeModal: NgbActiveModal) {
    super();
  }

  inputForm = new FormGroup({
    name: new FormControl(),
    nameEng: new FormControl(),
    nameIta: new FormControl(),
    nameSlo: new FormControl(),
    nameSr: new FormControl(),
    nameBs: new FormControl(),
    nameShort: new FormGroup({
      hr: new FormControl(),
      en: new FormControl(),
      it: new FormControl(),
      sl: new FormControl(),
      sr: new FormControl(),
      bs: new FormControl(),
    }),
    start: new FormControl(),
    end: new FormControl(),
    sportOne: new FormControl(),
    city: new FormControl(),
    country: new FormControl(),
    lengths: new FormControl(),
    image: new FormControl(),
    imageIcon: new FormControl(),
    leagueTemplateStatus: new FormControl(),
  });

  ngOnInit() {
    this.initForm();
    this.watchDates();
  }

  private initForm() {
    this.inputForm.controls.name.addValidators([required, max_length_255]);
    this.inputForm.controls.nameEng.addValidators([max_length_255]);
    this.inputForm.controls.nameIta.addValidators([max_length_255]);
    this.inputForm.controls.nameSlo.addValidators([max_length_255]);
    this.inputForm.controls.nameSr.addValidators([max_length_255]);
    this.inputForm.controls.nameBs.addValidators([max_length_255]);
    this.inputForm.controls.nameShort.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.hr.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.en.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.sl.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.it.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.sr.addValidators([max_length_100]);
    this.inputForm.controls.nameShort.controls.bs.addValidators([max_length_100]);
    this.inputForm.controls.start.addValidators([required]);
    this.inputForm.controls.sportOne.addValidators([required]);
    this.inputForm.controls.city.addValidators([required]);
    this.inputForm.controls.country.addValidators([required]);
    this.inputForm.controls.lengths.addValidators([required]);
    if (this.templateEvent) {
      this.inputForm.patchValue(this.templateEvent);
      this.updateStatus(true);
    }
  }

  private watchDates() {
    this.subscriptions.push(
        merge(
            this.inputForm.controls.start.valueChanges,
            this.inputForm.controls.end.valueChanges
        ).subscribe(() => this.updateStatus())
    );
  }

  private updateStatus(init: boolean = false) {
    const status = this.inputForm.controls.leagueTemplateStatus.value;
    const isManual = status === LeagueTemplateEventStatus.EXTERNAL || status === LeagueTemplateEventStatus.CANCELLED;

    const now = new Date();
    const end = this.inputForm.controls.end.value;
    const autoStatus = end && new Date(end) < now
        ? LeagueTemplateEventStatus.FINISHED
        : LeagueTemplateEventStatus.ACTIVE;

    this.leagueTemplateStatuses = [
      autoStatus,
      LeagueTemplateEventStatus.EXTERNAL,
      LeagueTemplateEventStatus.CANCELLED,
    ];

    if (!isManual || init && !isManual) {
      this.inputForm.controls.leagueTemplateStatus.setValue(autoStatus, { emitEvent: false });
    }
  }


  onClose(): void {
    this.activeModal.dismiss();
  }

  onSaveButtonClick(): void {
    if (!this.inputForm.valid) {
      this.markAllAsTouched(this.inputForm);
    }
    else {
      let event: EventItem = {...this.templateEvent}
      event.name = this.inputForm.value.name;
      event.nameEng = this.inputForm.value.nameEng;
      event.nameIta = this.inputForm.value.nameIta;
      event.nameSlo = this.inputForm.value.nameSlo;
      event.nameSr = this.inputForm.value.nameSr;
      event.nameBs = this.inputForm.value.nameBs;
      event.nameShort = {
        hr: this.inputForm.value.nameShort?.hr,
        en: this.inputForm.value.nameShort?.en,
        it: this.inputForm.value.nameShort?.it,
        sl: this.inputForm.value.nameShort?.sl,
        sr: this.inputForm.value.nameShort?.sr,
        bs: this.inputForm.value.nameShort?.bs,
      };
      event.start = this.inputForm.value.start;
      event.end = this.inputForm.value.end;
      event.sportOne = this.inputForm.value.sportOne;
      event.city = this.inputForm.value.city;
      event.country = this.inputForm.value.country;
      event.lengths = this.inputForm.value.lengths;
      event.image = this.inputForm.value.image;
      event.imageIcon = this.inputForm.value.imageIcon;
      event.leagueTemplateStatus = this.inputForm.value.leagueTemplateStatus;
      this.onSave.emit(event);
    }
  }

  removeTagAtIndex = (index: number) => {
    const tags = [...this.inputForm.controls.lengths.value];
    tags.splice(index, 1);
    this.inputForm.controls.lengths.setValue(tags);
  }

  lengthsChanged = (tags: string[] | undefined) => {
    const validUniqueNumbers = [
      ...new Set(
          (tags ?? []).filter(tag => {
            const value = tag.trim();
            return value !== '' && !isNaN(Number(value));
          })
      )
    ];

    this.inputForm.controls.lengths.setValue(validUniqueNumbers);
  };


  imageChanged(image: string | undefined) {
    this.inputForm.controls.image.setValue(image);
  }

  logoChanged(image: string | undefined) {
    this.inputForm.controls.imageIcon.setValue(image);
  }



}
